import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import api from '../../../services/api';
import FilterSelect from '../../../components/Selects/FilterSelect';
import { useAuth } from '../../../hooks/auth';
import { useModal } from '../../../hooks/modal';
import { useToast } from '../../../hooks/toast';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import TournamentManageModal from '../../../modals/TournamentManageModal';
import DisqualifyTeamModal from '../../../modals/DisqualifyTeamModal';
import ReinstateTeamModal from '../../../modals/ReinstateTeamModal';
import {
  TrophyIcon,
  PencilIcon,
  AddUserIcon,
  UserWithAddIcon,
  MiniBotIcon,
  BlobIllustration,
  GridLayoutIcon,
  ArrowBackIcon,
  SearchIcon,
  CrossIcon,
} from '../../../assets/svg/SVGComponents';
import {
  ActionButtonsRow,
  CategoryBadge,
  CategoryBadgeRow,
  Container,
  HeaderContent,
  LoadingWrapper,
  MainCard,
  MainCardContent,
  MedalCell,
  ModalityCard,
  ModalityImage,
  ModalityInfo,
  ModalityMeta,
  ModalityName,
  ModalitiesLabel,
  ModalitiesRow,
  NextSlotCard,
  NextSlotLabel,
  NextSlotMeta,
  NextSlotTeam,
  ClearFiltersLink,
  FilterField,
  FilterFieldLabel,
  FilterInput,
  FilterResultCount,
  FilterRow,
  FilterRowTitle,
  PrerequisiteChip,
  PrerequisiteChipRow,
  PrerequisitePill,
  RoundHeaderCell,
  RoundHeaderRow,
  RankingSection,
  RankingTable,
  RankingTableWrapper,
  RankingTitle,
  ScheduleEmptyCard,
  ScheduleEmptyDesc,
  ScheduleEmptyTitle,
  ScheduleHeader,
  ScheduleHeaderActions,
  ScheduleSection,
  ScheduleTable,
  ScheduleTableHead,
  ScheduleTableRow,
  ScheduleTableWrapper,
  ScheduleTd,
  ScheduleTh,
  ScrollHint,
  SlotStatusPill,
  Spinner,
  StatusAndHintsRow,
  Tab,
  TabBar,
  TournamentInfo,
  TournamentMeta,
  TournamentStatusBadge,
  TournamentTitle,
  TrophyImageWrapper,
  ViewRoundButton,
  ViewResultButton,
  DisqualifyButton,
} from './styles';
import {
  CATEGORY_NUMBER_TO_LABEL,
  MODALITY_SLUG_TO_NAME,
} from '../CreateTournament';
import {
  formatWallClockTime,
  formatTournamentDateRange,
} from '../../../utils/scheduleDate';

interface ITournamentCategory {
  id: string;
  number: number;
  name?: string;
}

interface ITournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  categories?: ITournamentCategory[];
}

interface IScheduleSlotArenaModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
}

interface IScheduleSlotArena {
  id: string;
  name: string;
  tournament_modality: IScheduleSlotArenaModality;
}

interface IScheduleSlotTeam {
  id: string;
  name: string;
  category: string;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface IScheduleSlotJudge {
  id: string;
  name: string;
}

interface IScheduleSlot {
  id: string;
  round_number: number;
  is_break: boolean;
  break_label: string | null;
  starts_at: string;
  ends_at: string;
  status: 'scheduled' | 'ongoing' | 'completed';
  total_score: number | null;
  arena: IScheduleSlotArena;
  team: IScheduleSlotTeam;
  judge: IScheduleSlotJudge | null;
}

interface IScheduleRound {
  id: string;
  team_id: string | null;
  team_name: string;
  arena: string;
  time: string;
  starts_at: string;
  modality: string;
  modality_slug: string;
  tournament_modality_id: string;
  category: string;
  judge: string;
  judge_id: string | null;
  round_number: number;
  status: string;
  max_rounds: number;
  is_break: boolean;
  break_label: string | null;
  total_score: number | null;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

interface IStandingEntry {
  team: {
    id: string;
    name: string;
    category: string;
    disqualified: boolean;
    disqualification_reason: string | null;
  };
  rounds_total: number;
  presentation_total: number;
  grand_total: number;
  best_time: number | null;
  rounds_scored: number;
  disqualified: boolean;
}

interface IRankingEntry {
  team_id: string;
  team_name: string;
  category: string;
  total_score: number;
  disqualified?: boolean;
  disqualification_reason?: string | null;
}

type TPrerequisiteKey = 'modalities' | 'arenas' | 'judges' | 'teams';

interface IPrerequisiteCheck {
  key: TPrerequisiteKey;
  satisfied: boolean;
  count: number;
  message?: string;
}

interface ISchedulePrerequisites {
  ready: boolean;
  checks: IPrerequisiteCheck[];
}

interface ITournamentDetails {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  location?: string | null;
  status?: string | null;
  modalities: ITournamentModality[];
  rounds?: IScheduleRound[];
  icon_url?: string | null;
  color?: string | null;
}

const TOURNAMENT_STATUS_LABEL: Record<string, string> = {
  planning: 'Em planejamento',
  planned: 'Em planejamento',
  open: 'Aberto',
  completed: 'Concluído',
};

const SLOT_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Agendado',
  ongoing: 'Em andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

const MODALITY_IMAGES: Record<string, string> = {
  robo_pintor:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Group+14045%402x.png',
  seguidor_de_linha:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/illustration-seguidor+de+trilha-2x.png',
};

const TROPHY_IMAGE =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/trophy-podium.png';

const PREREQUISITE_PILL_LABELS: Record<TPrerequisiteKey, string> = {
  modalities: 'Modalidades',
  arenas: 'Arenas',
  judges: 'Juízes',
  teams: 'Equipes',
};

const mapSlot = (slot: IScheduleSlot): IScheduleRound => {
  const categoryNum = parseInt(
    slot.team?.category?.replace('category_', '') ?? '',
    10,
  );
  const categoryLabel =
    CATEGORY_NUMBER_TO_LABEL[categoryNum] ?? slot.team?.category ?? '-';
  const modalitySlug = slot.arena?.tournament_modality?.modality ?? '';
  const modalityLabel =
    MODALITY_SLUG_TO_NAME[modalitySlug] ?? modalitySlug ?? '-';
  const timeLabel = slot.starts_at ? formatWallClockTime(slot.starts_at) : '-';
  return {
    id: slot.id,
    team_id: slot.team?.id ?? null,
    team_name: slot.team?.name ?? '-',
    arena: slot.arena?.name ?? '-',
    time: timeLabel,
    starts_at: slot.starts_at,
    modality: modalityLabel,
    modality_slug: modalitySlug,
    tournament_modality_id: slot.arena?.tournament_modality?.id ?? '',
    category: categoryLabel,
    judge: slot.judge?.name ?? '-',
    judge_id: slot.judge?.id ?? null,
    round_number: slot.round_number ?? 1,
    status:
      slot.total_score !== null ? 'completed' : (slot.status ?? 'scheduled'),
    max_rounds: slot.arena?.tournament_modality?.max_rounds ?? 1,
    is_break: slot.is_break ?? false,
    break_label: slot.break_label ?? null,
    total_score: slot.total_score ?? null,
    disqualified: slot.team?.disqualified ?? false,
    disqualification_reason: slot.team?.disqualification_reason ?? null,
  };
};

const formatRoundDuration = (timeLimitSeconds?: number): string => {
  if (!timeLimitSeconds || timeLimitSeconds <= 0) return '-';
  if (timeLimitSeconds < 60) return `${timeLimitSeconds}s`;
  const minutes = Math.floor(timeLimitSeconds / 60);
  const seconds = timeLimitSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds}s`;
};

const medalSymbol = (position: number): string => {
  if (position === 1) return '🥇';
  if (position === 2) return '🥈';
  if (position === 3) return '🥉';
  return String(position);
};

const TournamentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleModal } = useModal();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [tournament, setTournament] = useState<ITournamentDetails | null>(null);
  const [rounds, setRounds] = useState<IScheduleRound[]>([]);
  const [standings, setStandings] = useState<IStandingEntry[]>([]);
  const [prerequisites, setPrerequisites] =
    useState<ISchedulePrerequisites | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [filterMyRounds, setFilterMyRounds] = useState(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterMyRounds`) === 'true'
    );
  });
  const [activeTab, setActiveTab] = useState<'schedule' | 'ranking'>(
    'schedule',
  );
  const [filterTeam, setFilterTeam] = useState(() => {
    return localStorage.getItem(`@TournamentFilters:${id}:filterTeam`) || '';
  });
  const [filterModality, setFilterModality] = useState<string | null>(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterModality`) || null
    );
  });
  const [filterCategory, setFilterCategory] = useState<string | null>(() => {
    return (
      localStorage.getItem(`@TournamentFilters:${id}:filterCategory`) || null
    );
  });

  useEffect(() => {
    localStorage.setItem(
      `@TournamentFilters:${id}:filterMyRounds`,
      String(filterMyRounds),
    );
    localStorage.setItem(`@TournamentFilters:${id}:filterTeam`, filterTeam);
    if (filterModality) {
      localStorage.setItem(
        `@TournamentFilters:${id}:filterModality`,
        filterModality,
      );
    } else {
      localStorage.removeItem(`@TournamentFilters:${id}:filterModality`);
    }
    if (filterCategory) {
      localStorage.setItem(
        `@TournamentFilters:${id}:filterCategory`,
        filterCategory,
      );
    } else {
      localStorage.removeItem(`@TournamentFilters:${id}:filterCategory`);
    }
  }, [filterMyRounds, filterTeam, filterModality, filterCategory, id]);
  const [teamInputFocused, setTeamInputFocused] = useState(false);

  const fetchTournament = useCallback(
    async (showLoading = true) => {
      if (!id) return;
      if (showLoading) setIsLoading(true);

      try {
        const [
          { data: tournamentData },
          { data: scheduleData },
          { data: standingsData },
        ] = await Promise.all([
          api.get(`/tournaments/${id}`),
          api.get<{
            slots: IScheduleSlot[];
            prerequisites?: ISchedulePrerequisites;
          }>(`/tournaments/${id}/schedule`),
          api
            .get<IStandingEntry[]>(`/tournaments/${id}/rounds/standings`)
            .catch(() => ({ data: [] })),
        ]);

        setTournament(tournamentData);

        const safeStandings = Array.isArray(standingsData) ? standingsData : [];
        setStandings(safeStandings);

        const slots: IScheduleSlot[] = scheduleData?.slots ?? [];

        if (slots.length === 0) {
          setPrerequisites(scheduleData?.prerequisites ?? null);
          setRounds([]);
          return;
        }

        setPrerequisites(null);
        const mappedRounds = slots.map(mapSlot);
        const enrichedRounds = mappedRounds.map(r => {
          const s = safeStandings.find(st => st.team.id === r.team_id);
          return {
            ...r,
            disqualified: s?.disqualified ?? r.disqualified,
            disqualification_reason:
              s?.team?.disqualification_reason ?? r.disqualification_reason,
          };
        });
        setRounds(enrichedRounds);
      } catch {
        addToast({
          type: 'error',
          expression: 'Erro ao carregar',
          description: 'Não foi possível carregar os dados do torneio.',
        });
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [addToast, id],
  );

  const handleGenerateSchedule = useCallback(async () => {
    if (!id || !tournament?.date) return;
    setIsGenerating(true);
    try {
      const { data } = await api.post<{ slots: IScheduleSlot[] }>(
        `/tournaments/${id}/schedule`,
        {
          start_time: tournament.date,
          end_time: tournament.end_date ?? tournament.date,
        },
      );
      const slots = data?.slots ?? [];
      setPrerequisites(null);
      setRounds(slots.map(mapSlot));
      addToast({
        type: 'success',
        expression: 'Cronograma gerado!',
        description: `${slots.length} rodadas criadas com sucesso.`,
      });
    } catch (err: unknown) {
      const apiErrors = (
        err as {
          response?: { data?: { errors?: { key: string; message: string }[] } };
        }
      )?.response?.data?.errors;
      if (apiErrors?.length) {
        addToast({
          type: 'warn',
          expression: 'Pré-requisitos',
          description: apiErrors.map(e => e.message).join(' · '),
        });
      } else {
        addToast({
          type: 'error',
          expression: 'Erro ao gerar',
          description: 'Não foi possível gerar o cronograma. Tente novamente.',
        });
      }
    } finally {
      setIsGenerating(false);
    }
  }, [id, tournament, addToast]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  const nonBreakRounds = useMemo(
    () => rounds.filter(r => !r.is_break),
    [rounds],
  );

  const hasAnyResults = useMemo(
    () => nonBreakRounds.some(r => r.status === 'completed'),
    [nonBreakRounds],
  );

  const allCompleted = useMemo(
    () =>
      nonBreakRounds.length > 0 &&
      nonBreakRounds.every(r => r.status === 'completed'),
    [nonBreakRounds],
  );

  const nextSlot = useMemo(() => {
    const ongoingSlot = nonBreakRounds.find(r => r.status === 'ongoing');
    if (ongoingSlot) return { ...ongoingSlot, _isOngoing: true as const };
    const scheduled = nonBreakRounds
      .filter(r => r.status === 'scheduled')
      .sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
      );
    return scheduled[0]
      ? { ...scheduled[0], _isOngoing: false as const }
      : null;
  }, [nonBreakRounds]);

  const rankingByModality = useMemo(() => {
    if (standings.length === 0)
      return {} as Record<
        string,
        {
          modalityLabel: string;
          categoryLabel: string;
          entries: IRankingEntry[];
        }
      >;
    const grouped: Record<
      string,
      { modalityLabel: string; categoryLabel: string; entries: IRankingEntry[] }
    > = {};
    standings.forEach(st => {
      const teamRound = rounds.find(
        r => r.team_id === st.team.id && !r.is_break,
      );
      if (!teamRound) return;

      const key = `${teamRound.tournament_modality_id}_${st.team.category}`;
      if (!grouped[key]) {
        grouped[key] = {
          modalityLabel:
            MODALITY_SLUG_TO_NAME[teamRound.modality_slug] ??
            teamRound.modality_slug,
          categoryLabel: st.team.category,
          entries: [],
        };
      }
      grouped[key].entries.push({
        team_id: st.team.id,
        team_name: st.team.name,
        category: st.team.category,
        total_score: st.grand_total,
        disqualified: st.disqualified,
        disqualification_reason: st.team.disqualification_reason,
      });
    });
    return grouped;
  }, [standings, rounds]);

  const modalityOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    rounds.forEach(r => {
      if (!r.is_break && r.modality && !seen.has(r.modality)) {
        seen.add(r.modality);
        opts.push({ label: r.modality, value: r.modality });
      }
    });
    return opts;
  }, [rounds]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    rounds.forEach(r => {
      if (!r.is_break && r.category && !seen.has(r.category)) {
        seen.add(r.category);
        opts.push({ label: r.category, value: r.category });
      }
    });
    return opts;
  }, [rounds]);

  const handleOpenReinstateModal = useCallback(
    (teamId: string, teamName: string) => {
      handleModal(
        <ReinstateTeamModal
          tournamentId={id || ''}
          teamId={teamId}
          teamName={teamName}
          onSuccess={() => fetchTournament(false)}
        />,
      );
    },
    [id, handleModal, fetchTournament],
  );

  const handleOpenDisqualifyModal = useCallback(
    (teamId: string, teamName: string) => {
      handleModal(
        <DisqualifyTeamModal
          tournamentId={id || ''}
          teamId={teamId}
          teamName={teamName}
          onSuccess={() => fetchTournament(false)}
        />,
      );
    },
    [id, handleModal, fetchTournament],
  );

  const handleOpenManageModal = useCallback(
    (mode: 'edit' | 'teams' | 'judges' | 'arenas') => {
      if (!id) return;
      handleModal(
        <TournamentManageModal
          tournamentId={id}
          mode={mode}
          onUpdated={() => fetchTournament(false)}
        />,
      );
    },
    [fetchTournament, handleModal, id],
  );

  const hasActiveFilters =
    filterTeam.trim() !== '' ||
    filterModality !== null ||
    filterCategory !== null;

  const visibleRounds = useMemo(
    () =>
      rounds.filter(r => {
        if (r.is_break) return true;
        if (filterMyRounds && r.judge_id !== user.id) return false;
        if (
          filterTeam.trim() !== '' &&
          !r.team_name.toLowerCase().includes(filterTeam.trim().toLowerCase())
        )
          return false;
        if (filterModality !== null && r.modality !== filterModality)
          return false;
        if (filterCategory !== null && r.category !== filterCategory)
          return false;
        return true;
      }),
    [
      rounds,
      filterMyRounds,
      user.id,
      filterTeam,
      filterModality,
      filterCategory,
    ],
  );

  type TOrganizedItem =
    | { kind: 'round-header'; roundNumber: number; timeLabel: string }
    | { kind: 'slot'; data: IScheduleRound }
    | { kind: 'break'; data: IScheduleRound };

  const organizedSchedule = useMemo((): TOrganizedItem[] => {
    const sorted = [...visibleRounds].sort(
      (a, b) =>
        new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
    );
    const result: TOrganizedItem[] = [];
    let lastRound = -1;
    sorted.forEach(r => {
      if (r.is_break) {
        result.push({ kind: 'break', data: r });
      } else {
        if (r.round_number !== lastRound) {
          lastRound = r.round_number;
          result.push({
            kind: 'round-header',
            roundNumber: r.round_number,
            timeLabel: r.time,
          });
        }
        result.push({ kind: 'slot', data: r });
      }
    });
    return result;
  }, [visibleRounds]);

  const totalCols = 7 + (hasAnyResults ? 1 : 0) + 2;

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container>
        <p>Torneio não encontrado.</p>
      </Container>
    );
  }

  const dateLabel = formatTournamentDateRange(
    tournament.date,
    tournament.end_date,
  );

  return (
    <Container>
      <Link to={-1 as any}>
        <ArrowBackIcon
          width="1.33rem"
          stroke="#1E88E5"
          strokeWidth={3}
          fill="none"
        />
        Voltar
      </Link>

      <HeaderContent>
        <TrophyIcon width="2rem" stroke="#FF9800" strokeWidth={3} fill="none" />
        <h1>{tournament.name}</h1>
      </HeaderContent>

      <MainCard>
        <MainCardContent>
          <TrophyImageWrapper>
            <img src={tournament.icon_url ?? TROPHY_IMAGE} alt="Troféu" />
            <BlobIllustration
              width="100%"
              height="100%"
              fill={tournament.color ?? '#e0e0e0'}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </TrophyImageWrapper>

          <TournamentInfo>
            <TournamentTitle>{tournament.name}</TournamentTitle>
            {tournament.status && (
              <StatusAndHintsRow>
                <TournamentStatusBadge $status={tournament.status}>
                  {TOURNAMENT_STATUS_LABEL[tournament.status] ??
                    tournament.status}
                </TournamentStatusBadge>
                {prerequisites?.checks
                  .filter(c => !c.satisfied)
                  .map(check => (
                    <PrerequisitePill
                      key={check.key}
                      type="button"
                      onClick={() => {
                        if (check.key === 'teams')
                          navigate(`/tournament/${id}/teams`);
                        else if (check.key === 'arenas')
                          handleOpenManageModal('arenas');
                        else if (check.key === 'judges')
                          handleOpenManageModal('judges');
                        else handleOpenManageModal('edit');
                      }}
                    >
                      ⚠ {PREREQUISITE_PILL_LABELS[check.key]}
                    </PrerequisitePill>
                  ))}
              </StatusAndHintsRow>
            )}
            {dateLabel && <TournamentMeta>{dateLabel}</TournamentMeta>}
            {tournament.location && (
              <TournamentMeta>
                <strong>Local:</strong> {tournament.location}
              </TournamentMeta>
            )}

            <ModalitiesLabel>Modalidades:</ModalitiesLabel>
            <ModalitiesRow>
              {tournament.modalities.map(mod => {
                const modalityName =
                  MODALITY_SLUG_TO_NAME[mod.modality] ?? mod.modality;
                const imgSrc = MODALITY_IMAGES[mod.modality];
                const maxRoundsLabel =
                  typeof mod.max_rounds === 'number' ? mod.max_rounds : '-';
                const roundDurationLabel = formatRoundDuration(
                  mod.time_limit_seconds,
                );
                return (
                  <ModalityCard key={mod.id}>
                    {imgSrc && (
                      <ModalityImage src={imgSrc} alt={modalityName} />
                    )}
                    <ModalityInfo>
                      <ModalityName>{modalityName}</ModalityName>
                      <ModalityMeta>{`Rodadas: ${maxRoundsLabel}`}</ModalityMeta>
                      <ModalityMeta>{`Tempo por rodada: ${roundDurationLabel}`}</ModalityMeta>
                      <CategoryBadgeRow>
                        {mod.categories?.map(cat => (
                          <CategoryBadge key={cat.id}>
                            <MiniBotIcon
                              width="0.85rem"
                              strokeWidth={2}
                              stroke="#1e88e1"
                            />
                            {cat.name ??
                              CATEGORY_NUMBER_TO_LABEL[cat.number]?.split(
                                ':',
                              )[0] ??
                              `Categoria ${cat.number}`}
                          </CategoryBadge>
                        ))}
                      </CategoryBadgeRow>
                    </ModalityInfo>
                  </ModalityCard>
                );
              })}
            </ModalitiesRow>

            <ActionButtonsRow>
              <SecondaryButton
                type="button"
                onClick={() => handleOpenManageModal('edit')}
              >
                <PencilIcon
                  width="1rem"
                  stroke="#323232"
                  fill="none"
                  strokeWidth={2}
                />
                Editar
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => handleOpenManageModal('arenas')}
              >
                <GridLayoutIcon
                  width="1rem"
                  stroke="#323232"
                  fill="none"
                  strokeWidth={2}
                />
                Editar arenas
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => navigate(`/tournament/${id}/teams`)}
              >
                <AddUserIcon
                  width="1rem"
                  stroke="#323232"
                  fill="none"
                  strokeWidth={2}
                />
                Adicionar equipe
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => handleOpenManageModal('judges')}
              >
                <UserWithAddIcon
                  width="1rem"
                  stroke="#323232"
                  fill="none"
                  strokeWidth={2}
                />
                Adicionar juiz
              </SecondaryButton>
            </ActionButtonsRow>
          </TournamentInfo>
        </MainCardContent>
      </MainCard>

      <ScheduleSection>
        <ScheduleHeader>
          <h2>
            {activeTab === 'ranking' ? 'Resultado final' : 'Quadro de horários'}
          </h2>
          <ScheduleHeaderActions>
            <SecondaryButton
              type="button"
              onClick={() => setFilterMyRounds(prev => !prev)}
              style={
                filterMyRounds
                  ? { borderColor: '#FF9800', color: '#FF9800' }
                  : undefined
              }
            >
              {filterMyRounds
                ? 'Ver todas as rodadas'
                : 'Visualizar minhas rodadas'}
            </SecondaryButton>
            {!allCompleted && (
              <PrimaryButton
                type="button"
                onClick={() => navigate(`/tournament/${id}/schedule/edit`)}
              >
                <TrophyIcon
                  width="1rem"
                  stroke="#fff"
                  strokeWidth={3}
                  fill="none"
                />
                Editar Cronograma
              </PrimaryButton>
            )}
          </ScheduleHeaderActions>
        </ScheduleHeader>

        {/* Estado 2: card de próxima / rodada em andamento */}
        {hasAnyResults &&
          !allCompleted &&
          nextSlot &&
          activeTab === 'schedule' && (
            <NextSlotCard>
              <NextSlotLabel>
                {nextSlot._isOngoing ? '🔴 Em andamento' : '⏱ Próxima Rodada'}
              </NextSlotLabel>
              <NextSlotTeam>{nextSlot.team_name}</NextSlotTeam>
              <NextSlotMeta>
                {nextSlot.arena}
                {nextSlot.judge !== '-' ? ` · Juiz: ${nextSlot.judge}` : ''}
                {' · '}
                {nextSlot.time}
              </NextSlotMeta>
            </NextSlotCard>
          )}

        {/* Estado 3: tabs */}
        {hasAnyResults && (
          <TabBar>
            <Tab
              $active={activeTab === 'schedule'}
              onClick={() => setActiveTab('schedule')}
            >
              Cronograma
            </Tab>
            <Tab
              $active={activeTab === 'ranking'}
              onClick={() => setActiveTab('ranking')}
            >
              Classificação Final
            </Tab>
          </TabBar>
        )}

        {/* Tabela (estados 1, 2 e aba Cronograma do estado 3) */}
        {(!hasAnyResults || activeTab === 'schedule') && (
          <>
            <ScrollHint>← Deslize para ver mais →</ScrollHint>

            {rounds.length > 0 && (
              <FilterRow>
                <FilterRowTitle>Filtros</FilterRowTitle>
                <FilterField>
                  <FilterFieldLabel>Equipe</FilterFieldLabel>
                  <FilterInput $isFocused={teamInputFocused}>
                    <SearchIcon
                      width="1rem"
                      stroke={teamInputFocused ? '#1e88e5' : '#9ca3af'}
                      fill="none"
                      strokeWidth={2}
                    />
                    <input
                      placeholder="Buscar equipe..."
                      value={filterTeam}
                      onChange={e => setFilterTeam(e.target.value)}
                      onFocus={() => setTeamInputFocused(true)}
                      onBlur={() => setTeamInputFocused(false)}
                    />
                  </FilterInput>
                </FilterField>

                <FilterField>
                  <FilterFieldLabel>Modalidade</FilterFieldLabel>
                  <FilterSelect
                    name="filter-modality"
                    placeholder="Todas"
                    options={modalityOptions}
                    value={
                      filterModality
                        ? { label: filterModality, value: filterModality }
                        : null
                    }
                    onChange={(opt: { label: string; value: string } | null) =>
                      setFilterModality(opt ? opt.value : null)
                    }
                    isClearable
                    isSearchable={false}
                    styles={{
                      control: (
                        base: object,
                        state: { isFocused: boolean },
                      ) => ({
                        ...base,
                        width: '200px',
                        height: '2.75rem',
                        minHeight: '2.75rem',
                        borderRadius: '0.55rem',
                        borderColor: state.isFocused ? '#1e88e5' : '#d1d5db',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused
                          ? '0 0 0 3px rgba(30, 136, 229, 0.1)'
                          : 'none',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#1e88e5' },
                      }),
                      placeholder: (base: object) => ({
                        ...base,
                        fontSize: '0.88rem',
                        color: '#9ca3af',
                      }),
                      singleValue: (base: object) => ({
                        ...base,
                        fontSize: '0.88rem',
                        color: '#323232',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                  />
                </FilterField>

                <FilterField>
                  <FilterFieldLabel>Categoria</FilterFieldLabel>
                  <FilterSelect
                    name="filter-category"
                    placeholder="Todas"
                    options={categoryOptions}
                    value={
                      filterCategory
                        ? { label: filterCategory, value: filterCategory }
                        : null
                    }
                    onChange={(opt: { label: string; value: string } | null) =>
                      setFilterCategory(opt ? opt.value : null)
                    }
                    isClearable
                    isSearchable={false}
                    styles={{
                      control: (
                        base: object,
                        state: { isFocused: boolean },
                      ) => ({
                        ...base,
                        width: '200px',
                        height: '2.75rem',
                        minHeight: '2.75rem',
                        borderRadius: '0.55rem',
                        borderColor: state.isFocused ? '#1e88e5' : '#d1d5db',
                        borderWidth: '1.5px',
                        boxShadow: state.isFocused
                          ? '0 0 0 3px rgba(30, 136, 229, 0.1)'
                          : 'none',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: '#1e88e5' },
                      }),
                      placeholder: (base: object) => ({
                        ...base,
                        fontSize: '0.88rem',
                        color: '#9ca3af',
                      }),
                      singleValue: (base: object) => ({
                        ...base,
                        fontSize: '0.88rem',
                        color: '#323232',
                      }),
                      indicatorSeparator: () => ({ display: 'none' }),
                    }}
                  />
                </FilterField>

                {hasActiveFilters && (
                  <>
                    <FilterResultCount>
                      {visibleRounds.filter(r => !r.is_break).length} de{' '}
                      {nonBreakRounds.length} rodadas
                    </FilterResultCount>
                    <ClearFiltersLink
                      type="button"
                      onClick={() => {
                        setFilterTeam('');
                        setFilterModality(null);
                        setFilterCategory(null);
                      }}
                    >
                      <CrossIcon
                        width="0.65rem"
                        stroke="#dc2626"
                        fill="none"
                        strokeWidth={2.5}
                      />
                      Limpar
                    </ClearFiltersLink>
                  </>
                )}
              </FilterRow>
            )}

            {rounds.length === 0 ? (
              <ScheduleEmptyCard>
                {prerequisites?.ready ? (
                  <>
                    <ScheduleEmptyTitle>
                      Tudo pronto! Gere o cronograma para começar.
                    </ScheduleEmptyTitle>
                    <ScheduleEmptyDesc>
                      Equipes, arenas e juízes configurados. O cronograma será
                      gerado automaticamente com base nessas informações.
                    </ScheduleEmptyDesc>
                    {!tournament?.date && (
                      <ScheduleEmptyDesc style={{ color: '#f59e0b' }}>
                        Defina as datas do torneio antes de gerar o cronograma.
                      </ScheduleEmptyDesc>
                    )}
                    <PrimaryButton
                      type="button"
                      loading={isGenerating}
                      disabled={isGenerating || !tournament?.date}
                      onClick={handleGenerateSchedule}
                    >
                      <TrophyIcon
                        width="1rem"
                        stroke="#fff"
                        strokeWidth={3}
                        fill="none"
                      />
                      Gerar Cronograma
                    </PrimaryButton>
                  </>
                ) : prerequisites && !prerequisites.ready ? (
                  <>
                    <ScheduleEmptyTitle>
                      Configure o torneio para gerar o cronograma
                    </ScheduleEmptyTitle>
                    <ScheduleEmptyDesc>
                      Adicione os itens abaixo para liberar a geração automática
                      do cronograma:
                    </ScheduleEmptyDesc>
                    <PrerequisiteChipRow>
                      {prerequisites.checks
                        .filter(c => !c.satisfied)
                        .map(check => (
                          <PrerequisiteChip
                            key={check.key}
                            type="button"
                            onClick={() => {
                              if (check.key === 'teams')
                                navigate(`/tournament/${id}/teams`);
                              else if (check.key === 'arenas')
                                handleOpenManageModal('arenas');
                              else if (check.key === 'judges')
                                handleOpenManageModal('judges');
                              else handleOpenManageModal('edit');
                            }}
                          >
                            + {PREREQUISITE_PILL_LABELS[check.key]}
                          </PrerequisiteChip>
                        ))}
                    </PrerequisiteChipRow>
                    <ScheduleEmptyDesc style={{ fontSize: '0.75rem' }}>
                      {prerequisites.checks.filter(c => c.satisfied).length}/
                      {prerequisites.checks.length} itens configurados
                    </ScheduleEmptyDesc>
                  </>
                ) : (
                  <ScheduleEmptyDesc>
                    Nenhuma rodada cadastrada ainda.
                  </ScheduleEmptyDesc>
                )}
              </ScheduleEmptyCard>
            ) : (
              <ScheduleTableWrapper>
                <ScheduleTable>
                  <ScheduleTableHead>
                    <tr>
                      <ScheduleTh>Equipe</ScheduleTh>
                      <ScheduleTh>Arena</ScheduleTh>
                      <ScheduleTh>Horário</ScheduleTh>
                      <ScheduleTh>Modalidade</ScheduleTh>
                      <ScheduleTh>Categoria</ScheduleTh>
                      <ScheduleTh>Juiz</ScheduleTh>
                      <ScheduleTh>Status</ScheduleTh>
                      {hasAnyResults && <ScheduleTh>Pontuação</ScheduleTh>}
                      <ScheduleTh>Avaliação</ScheduleTh>
                      <ScheduleTh>Ações</ScheduleTh>
                    </tr>
                  </ScheduleTableHead>
                  <tbody>
                    {organizedSchedule.map(item => {
                      if (item.kind === 'round-header') {
                        return (
                          <RoundHeaderRow key={`rh-${item.roundNumber}`}>
                            <RoundHeaderCell colSpan={totalCols}>
                              Rodada {item.roundNumber}
                              <span
                                style={{
                                  fontFamily: "'RedHatDisplay-Regular'",
                                  opacity: 0.65,
                                  marginLeft: '0.5rem',
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  letterSpacing: 0,
                                }}
                              >
                                · {item.timeLabel}
                              </span>
                            </RoundHeaderCell>
                          </RoundHeaderRow>
                        );
                      }

                      const round = item.data;

                      if (item.kind === 'break') {
                        return (
                          <tr key={round.id}>
                            <ScheduleTd
                              colSpan={totalCols}
                              style={{
                                textAlign: 'center',
                                fontStyle: 'italic',
                                color: '#9ca3af',
                                background: '#fafafa',
                                fontSize: '0.85rem',
                              }}
                            >
                              {round.break_label ?? 'Intervalo'} · {round.time}
                            </ScheduleTd>
                          </tr>
                        );
                      }

                      return (
                        <ScheduleTableRow
                          key={round.id}
                          $status={round.status}
                          style={
                            round.disqualified
                              ? { background: '#fef2f2', opacity: 0.85 }
                              : undefined
                          }
                        >
                          <ScheduleTd>
                            <span
                              style={{
                                textDecoration: round.disqualified
                                  ? 'line-through'
                                  : 'none',
                                color: round.disqualified
                                  ? '#ef4444'
                                  : 'inherit',
                              }}
                            >
                              {round.team_name}
                            </span>
                          </ScheduleTd>
                          <ScheduleTd>{round.arena}</ScheduleTd>
                          <ScheduleTd>{round.time}</ScheduleTd>
                          <ScheduleTd>{round.modality}</ScheduleTd>
                          <ScheduleTd>{round.category}</ScheduleTd>
                          <ScheduleTd>{round.judge}</ScheduleTd>
                          <ScheduleTd>
                            <SlotStatusPill $status={round.status}>
                              {SLOT_STATUS_LABEL[round.status] ?? round.status}
                            </SlotStatusPill>
                          </ScheduleTd>
                          {hasAnyResults && (
                            <ScheduleTd>
                              {round.disqualified ? (
                                <span
                                  style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: '#ef4444',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                  }}
                                >
                                  Desclassificado
                                </span>
                              ) : round.total_score !== null ? (
                                `${round.total_score} pts`
                              ) : (
                                '—'
                              )}
                            </ScheduleTd>
                          )}
                          <ScheduleTd>
                            {round.status === 'completed' ? (
                              <ViewResultButton
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/tournament/${id}/rounds/${round.id}?round=${round.round_number}&view=result`,
                                  )
                                }
                              >
                                Ver resultado
                              </ViewResultButton>
                            ) : (
                              <ViewRoundButton
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/tournament/${id}/rounds/${round.id}?round=${round.round_number}`,
                                  )
                                }
                              >
                                Rodada {round.round_number}
                              </ViewRoundButton>
                            )}
                          </ScheduleTd>
                          <ScheduleTd>
                            {round.disqualified ? (
                              <ViewResultButton
                                type="button"
                                onClick={() =>
                                  handleOpenReinstateModal(
                                    round.team_id!,
                                    round.team_name,
                                  )
                                }
                                style={{
                                  padding: '0.2rem 0.5rem',
                                  height: 'auto',
                                  fontSize: '0.7rem',
                                  color: '#16a34a',
                                  borderColor: '#16a34a',
                                }}
                              >
                                Reverter
                              </ViewResultButton>
                            ) : (
                              <DisqualifyButton
                                type="button"
                                onClick={() =>
                                  handleOpenDisqualifyModal(
                                    round.team_id!,
                                    round.team_name,
                                  )
                                }
                                style={{
                                  padding: '0.2rem 0.5rem',
                                  height: 'auto',
                                  fontSize: '0.7rem',
                                  color: '#ef4444',
                                  borderColor: '#ef4444',
                                }}
                              >
                                Desclassificar
                              </DisqualifyButton>
                            )}
                          </ScheduleTd>
                        </ScheduleTableRow>
                      );
                    })}
                  </tbody>
                </ScheduleTable>
              </ScheduleTableWrapper>
            )}
          </>
        )}

        {/* Aba Classificação Final (estado 3) */}
        {hasAnyResults && activeTab === 'ranking' && (
          <>
            {Object.entries(rankingByModality).map(
              ([key, { modalityLabel, categoryLabel, entries }]) => (
                <RankingSection key={key}>
                  <RankingTitle>
                    🏆 {modalityLabel} - {categoryLabel}
                  </RankingTitle>
                  <RankingTableWrapper>
                    <RankingTable>
                      <thead>
                        <tr>
                          <th style={{ width: '3rem' }}>#</th>
                          <th>Equipe</th>
                          <th>Categoria</th>
                          <th>Pontuação Total</th>
                          <th style={{ width: '8rem' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, i) => (
                          <tr
                            key={entry.team_id}
                            style={{
                              opacity: entry.disqualified ? 0.6 : 1,
                              background: entry.disqualified
                                ? '#fef2f2'
                                : 'transparent',
                            }}
                          >
                            <MedalCell
                              $position={entry.disqualified ? 999 : i + 1}
                            >
                              {entry.disqualified ? '⚠️' : medalSymbol(i + 1)}
                            </MedalCell>
                            <td>
                              <span
                                style={{
                                  textDecoration: entry.disqualified
                                    ? 'line-through'
                                    : 'none',
                                }}
                              >
                                {entry.team_name}
                              </span>
                            </td>
                            <td>{entry.category}</td>
                            <td>
                              {entry.disqualified ? (
                                <span
                                  style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    color: '#ef4444',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.03em',
                                  }}
                                  title={entry.disqualification_reason || ''}
                                >
                                  Desclassificado
                                </span>
                              ) : (
                                <strong>{entry.total_score} pts</strong>
                              )}
                            </td>
                            <td>
                              {entry.disqualified ? (
                                <SecondaryButton
                                  type="button"
                                  onClick={() =>
                                    handleOpenReinstateModal(
                                      entry.team_id,
                                      entry.team_name,
                                    )
                                  }
                                  style={{
                                    padding: '0.2rem 0.5rem',
                                    height: 'auto',
                                    fontSize: '0.75rem',
                                    color: '#16a34a',
                                    borderColor: '#16a34a',
                                  }}
                                >
                                  Reverter
                                </SecondaryButton>
                              ) : (
                                <SecondaryButton
                                  type="button"
                                  onClick={() =>
                                    handleOpenDisqualifyModal(
                                      entry.team_id,
                                      entry.team_name,
                                    )
                                  }
                                  style={{
                                    padding: '0.2rem 0.5rem',
                                    height: 'auto',
                                    fontSize: '0.75rem',
                                    color: '#ef4444',
                                    borderColor: '#ef4444',
                                  }}
                                >
                                  Desclassificar
                                </SecondaryButton>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </RankingTable>
                  </RankingTableWrapper>
                </RankingSection>
              ),
            )}
          </>
        )}
      </ScheduleSection>
    </Container>
  );
};

export default TournamentDetails;
